import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  signUp,
} from 'aws-amplify/auth';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import {
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getOthRegistrationSchema,
  OthRegistrationField,
  OthRegistrationSchema,
  OthRegistrationSection,
  submitOthRegistration,
  SubmitOthRegistrationResult,
} from 'src/api/othRegistration';
import { usersApi } from 'src/api/user';
import { CircleBrandingScope, useBranding } from 'src/branding';
import { DunbarPhoneInput } from 'src/components/common/PhoneInput/PhoneInput';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { MuiModal } from 'src/components/material/MuiModal';
import { Oth360Banner } from 'src/components/oth/Oth360Banner';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { useUserLoginLogout } from 'src/hooks/UserLoginLogout/UserLoginLogout';
import { fetchLoggedUser } from 'src/redux/slices/loggedUserSlice';
import {
  clearPublicBranding,
  setPublicBranding,
} from 'src/redux/slices/publicBrandingSlice';
import { fetchUser } from 'src/redux/slices/userSlice';
import { themeColors } from 'src/theme';
import { Utils } from 'src/utils/utils';

import styles from './OthRegistrationPage.module.scss';

type MemberDraft = {
  full_name: string;
  first_name: string;
  surname: string;
  firm: string;
  phone_number: string;
  email: string;
  password: string;
  confirmationCode: string;
};

const isRequiredCheckboxChecked = (value: unknown): boolean => value === true;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const getStringValue = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const splitFullName = (
  fullName: string
): { firstName: string; surname: string } => {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { firstName: '', surname: '' };
  }

  const [firstName, ...rest] = trimmed.split(/\s+/);

  return {
    firstName,
    surname: rest.join(' '),
  };
};

const isPasswordStrong = (value: string): boolean =>
  value.length >= 8 &&
  /[0-9]/.test(value) &&
  /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/.test(value);

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

enum AccountStep {
  SignupRequest = 'SignupRequest',
  SignupVerification = 'SignupVerification',
  Ready = 'Ready',
}

const STEP_LABELS: Record<string, string> = {
  aboutYou: 'Om deg',
  invoice: 'Fakturainformasjon',
  preferences: 'Preferanser',
  terms: 'Betingelser',
};

const OM_DEG_MANAGED_FIELD_IDS = [
  'first_name',
  'surname',
  'email',
  'phone_number',
];
const HIDDEN_SECTION_IDS = new Set(['terms']);
const HIDDEN_FIELD_IDS = new Set(['fill_consent_later']);
const HOTEL_QUESTION_TAIL = /\s*til alle samlingene på Farris Bad\s*/gi;

const sanitizeRegistrationFieldLabel = (label: string): string =>
  label
    .replace(HOTEL_QUESTION_TAIL, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

const sanitizeRegistrationSchema = (
  schema: OthRegistrationSchema
): OthRegistrationSchema => ({
  ...schema,
  schemaJson: {
    ...schema.schemaJson,
    sections: schema.schemaJson.sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        label: sanitizeRegistrationFieldLabel(field.label),
      })),
    })),
  },
});
// Fields in the hidden "terms" section that are managed by dedicated UI state
// (acceptedProgramTerms / acceptedTerms) — skip these in schema-driven validation.
const TERMS_MANAGED_FIELD_IDS = new Set(['accept_terms_1', 'accept_terms_2']);

// Consent checkbox fields that must be actively re-accepted each session.
// Cleared from draft on restore so stale values don't bypass validation.
const CONSENT_FIELD_IDS = new Set(['consent_1', 'consent_2', 'consent_3']);

const TOAST_DURATION = 2500;

const ToastContent = ({
  message,
  toastId,
}: {
  message: string;
  toastId: string;
}) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    {message}
    <button
      type="button"
      onClick={() => toast.dismiss(toastId)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 18,
        lineHeight: 1,
        padding: 0,
        color: 'inherit',
        flexShrink: 0,
      }}
      aria-label="Lukk"
    >
      ✕
    </button>
  </span>
);

const showError = (msg: string) =>
  toast.error((t) => <ToastContent message={msg} toastId={t.id} />, {
    duration: TOAST_DURATION,
  });
const showSuccess = (msg: string) =>
  toast.success((t) => <ToastContent message={msg} toastId={t.id} />, {
    duration: TOAST_DURATION,
  });

const getAllFields = (
  sections: OthRegistrationSection[]
): OthRegistrationField[] =>
  sections.flatMap((s) => s.fields.filter((f) => !HIDDEN_FIELD_IDS.has(f.id)));

const DEFAULT_MEMBER_DRAFT: MemberDraft = {
  full_name: '',
  first_name: '',
  surname: '',
  firm: '',
  phone_number: '',
  email: '',
  password: '',
  confirmationCode: '',
};

type OthRegistrationDraft = {
  member: MemberDraft;
  answers: Record<string, unknown>;
  accountStep: AccountStep;
  showVerification: boolean;
  acceptedProgramTerms: boolean;
  acceptedTerms: boolean;
  currentStep: number;
};

const getRegistrationDraftStorageKey = (tribeId: string): string =>
  `othRegistrationDraft:${tribeId}`;

const readRegistrationDraft = (
  tribeId: string
): OthRegistrationDraft | null => {
  if (!tribeId) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(getRegistrationDraftStorageKey(tribeId));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<OthRegistrationDraft>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      member: {
        ...DEFAULT_MEMBER_DRAFT,
        ...(parsed.member ?? {}),
      },
      answers:
        parsed.answers && typeof parsed.answers === 'object'
          ? Object.fromEntries(
              Object.entries(parsed.answers).map(([k, v]) => [
                k,
                CONSENT_FIELD_IDS.has(k) ? false : v,
              ])
            )
          : {},
      accountStep:
        parsed.accountStep === AccountStep.SignupVerification ||
        parsed.accountStep === AccountStep.Ready
          ? parsed.accountStep
          : AccountStep.SignupRequest,
      showVerification: Boolean(parsed.showVerification),
      acceptedProgramTerms: Boolean(parsed.acceptedProgramTerms),
      acceptedTerms: Boolean(parsed.acceptedTerms),
      currentStep:
        typeof parsed.currentStep === 'number' && parsed.currentStep >= 0
          ? parsed.currentStep
          : 0,
    };
  } catch {
    return null;
  }
};

export const OthRegistrationPage = () => {
  const dispatch = useAppDispatch();
  const { login } = useUserLoginLogout();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const { tribeId = '' } = useParams<{ tribeId: string }>();
  const initialDraft = useMemo(() => readRegistrationDraft(tribeId), [tribeId]);

  const [schema, setSchema] = useState<OthRegistrationSchema | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [member, setMember] = useState<MemberDraft>(
    initialDraft?.member ?? DEFAULT_MEMBER_DRAFT
  );
  const [answers, setAnswers] = useState<Record<string, unknown>>(
    initialDraft?.answers ?? {}
  );
  const [accountStep, setAccountStep] = useState<AccountStep>(
    initialDraft?.accountStep ?? AccountStep.SignupRequest
  );
  const [isCheckingAccount, setIsCheckingAccount] = useState(true);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] =
    useState<SubmitOthRegistrationResult | null>(null);

  // Whether the 2FA interstitial is shown (between "Om deg" tab and "Faktura" tab)
  const [showVerification, setShowVerification] = useState(
    initialDraft?.showVerification ?? false
  );

  // Terms / privacy modals
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [acceptedProgramTerms, setAcceptedProgramTerms] = useState(
    initialDraft?.acceptedProgramTerms ?? false
  );
  const [acceptedTerms, setAcceptedTerms] = useState(
    initialDraft?.acceptedTerms ?? false
  );

  // Collapsible "Praktisk informasjon" – tribe_name/description from schema or fallback
  const [infoExpanded, setInfoExpanded] = useState(false);

  // ─── Step model ────────────────────────────────────────────────
  // Steps: 0 = "Om deg" (merged auth + aboutYou), 1..N-1 = remaining schema sections, N = summary
  // The "aboutYou" section is rendered on step 0 together with auth fields,
  // so the remaining sections start at index 1.

  const [currentStep, setCurrentStep] = useState(
    initialDraft?.currentStep ?? 0
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sections = schema?.schemaJson.sections ?? [];
  const visibleSections = sections.filter((s) => !HIDDEN_SECTION_IDS.has(s.id));
  const aboutYouSection =
    visibleSections.find((s) => s.id === 'aboutYou') ?? null;
  const remainingSections = visibleSections.filter((s) => s.id !== 'aboutYou');
  // total tabs: "Om deg"(0) + remaining sections (1..R) + summary (R+1)
  const totalSteps = remainingSections.length + 2;
  const summaryStepIndex = totalSteps - 1;

  const stepLabels = useMemo(() => {
    const labels = [STEP_LABELS['aboutYou'] ?? 'Om deg'];
    for (const section of remainingSections) {
      labels.push(STEP_LABELS[section.id] ?? section.title);
    }
    labels.push('Sammendrag');
    return labels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingSections]);

  // The schema section being shown on steps 1..N-1
  const currentSection: OthRegistrationSection | null =
    currentStep > 0 && currentStep < summaryStepIndex
      ? (remainingSections[currentStep - 1] ?? null)
      : null;

  // Persist draft so form data survives auth-driven remounts.
  useEffect(() => {
    if (!tribeId) {
      return;
    }

    const draft: OthRegistrationDraft = {
      member,
      answers,
      accountStep,
      showVerification,
      acceptedProgramTerms,
      acceptedTerms,
      currentStep,
    };
    sessionStorage.setItem(
      getRegistrationDraftStorageKey(tribeId),
      JSON.stringify(draft)
    );
  }, [
    tribeId,
    member,
    answers,
    accountStep,
    showVerification,
    acceptedProgramTerms,
    acceptedTerms,
    currentStep,
  ]);

  const waitForBackendUserRecord = useCallback(async () => {
    let lastError: unknown = null;

    for (let attempt = 0; attempt < 20; attempt += 1) {
      try {
        return await usersApi.userMe();
      } catch (error) {
        lastError = error;
        await sleep(500);
      }
    }

    throw lastError ?? new Error('Could not initialize your account profile');
  }, []);

  const hydrateMemberFromBackend = useCallback(async () => {
    const profileResponse = await waitForBackendUserRecord();
    const profile = profileResponse.result;
    const { firstName, surname } = splitFullName(profile.full_name || '');

    setMember((prev) => ({
      ...prev,
      full_name: prev.full_name || profile.full_name || '',
      first_name: prev.first_name || firstName,
      surname: prev.surname || surname,
      phone_number: prev.phone_number || profile.phone_number || '',
      email: prev.email || profile.email || '',
    }));
  }, [waitForBackendUserRecord]);

  const completeLoginAndBootstrap = useCallback(async () => {
    const formattedPhoneNumber = Utils.formatPhone(member.phone_number);
    const result = await login({
      username: formattedPhoneNumber,
      password: member.password,
    });

    if (!result?.isSignedIn) {
      throw new Error('Login failed after confirmation');
    }

    await dispatch(fetchUser());
    await waitForBackendUserRecord();
    await hydrateMemberFromBackend();
    await dispatch(fetchLoggedUser());
    setAccountStep(AccountStep.Ready);
  }, [
    member.phone_number,
    member.password,
    login,
    dispatch,
    waitForBackendUserRecord,
    hydrateMemberFromBackend,
  ]);

  // Load schema and tribe info
  useEffect(() => {
    const loadSchema = async () => {
      if (!tribeId) {
        setLoadError('Missing circle id');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setLoadError(null);
        const result = await getOthRegistrationSchema(tribeId);
        setSchema(sanitizeRegistrationSchema(result));
        // Presentation only: lets this page and the top bar wear the
        // circle's look (see useBranding)
        dispatch(
          setPublicBranding({
            circleId: tribeId,
            branding: result.branding ?? null,
          })
        );
      } catch (error) {
        setLoadError('Registration is currently unavailable for this circle.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSchema();
  }, [tribeId, dispatch]);

  useEffect(
    () => () => {
      dispatch(clearPublicBranding());
    },
    [dispatch]
  );

  // The circle's branding wraps what this page renders, so state that lives
  // in this component survives the switch once the branding has loaded
  const branding = useBranding();
  const withBrand = (page: ReactElement) => (
    <CircleBrandingScope branding={branding}>{page}</CircleBrandingScope>
  );

  // Check existing session
  useEffect(() => {
    let isMounted = true;

    const checkCurrentSession = async () => {
      try {
        await getCurrentUser();
        if (!isMounted) {
          return;
        }

        setAccountStep(AccountStep.Ready);
        // Stay on step 0 (Om deg) so user can review fields
        try {
          await hydrateMemberFromBackend();
        } catch {
          // Keep the tab usable even if profile hydration is delayed.
        }
      } catch {
        if (!isMounted) {
          return;
        }

        setAccountStep((prev) =>
          prev === AccountStep.SignupVerification
            ? AccountStep.SignupVerification
            : AccountStep.SignupRequest
        );
      } finally {
        if (isMounted) {
          setIsCheckingAccount(false);
        }
      }
    };

    checkCurrentSession();

    return () => {
      isMounted = false;
    };
  }, [hydrateMemberFromBackend]);

  // Sync full_name → first_name / surname
  useEffect(() => {
    if (member.first_name || member.surname || !member.full_name.trim()) {
      return;
    }

    const { firstName, surname } = splitFullName(member.full_name);
    setMember((prev) => ({
      ...prev,
      first_name: firstName,
      surname,
    }));
  }, [member.first_name, member.full_name, member.surname]);

  // Initialise answers for all schema fields
  useEffect(() => {
    if (!schema) {
      return;
    }

    setAnswers((prev) => {
      const next = { ...prev };
      for (const section of schema.schemaJson.sections) {
        for (const field of section.fields) {
          if (next[field.id] === undefined) {
            next[field.id] = field.type === 'checkbox' ? false : '';
          }
        }
      }
      return next;
    });
  }, [schema]);

  // Keep schema answers in sync with the Om deg fields. Those inputs write
  // to `member`, while summary/submit read `answers`.
  useEffect(() => {
    setAnswers((prev) => ({
      ...prev,
      first_name: member.first_name || prev.first_name || '',
      surname: member.surname || prev.surname || '',
      email: member.email || prev.email || '',
      phone_number: member.phone_number || prev.phone_number || '',
      firm: member.firm || prev.firm || '',
    }));
  }, [
    member.first_name,
    member.surname,
    member.email,
    member.phone_number,
    member.firm,
  ]);

  // ─── Validation ────────────────────────────────────────────────

  const validationErrors = useMemo(() => {
    if (!schema) {
      return [];
    }

    const errors: string[] = [];
    // Scan ALL sections (including hidden ones like "terms") so that consent
    // checkboxes are validated regardless of which section they live in.
    const allFields = getAllFields(sections);

    for (const field of allFields) {
      if (!field.required) {
        continue;
      }

      // Skip fields handled by dedicated UI state
      if (TERMS_MANAGED_FIELD_IDS.has(field.id)) {
        continue;
      }

      const answer = answers[field.id];
      if (field.type === 'checkbox') {
        if (!isRequiredCheckboxChecked(answer)) {
          errors.push(`${field.label} må aksepteres.`);
        }
        continue;
      }

      if (!isNonEmptyString(answer)) {
        errors.push(`${field.label} er påkrevd.`);
        continue;
      }

      if (
        (field.type === 'select' || field.type === 'radio') &&
        Array.isArray(field.options) &&
        !field.options.includes(answer)
      ) {
        errors.push(`${field.label} har en ugyldig verdi.`);
      }
    }

    return errors;
  }, [answers, schema, sections]);

  const getFieldValidationError = useCallback(
    (field: OthRegistrationField): string | null => {
      if (!field.required) {
        return null;
      }

      const answer = answers[field.id];
      if (field.type === 'checkbox') {
        if (!isRequiredCheckboxChecked(answer)) {
          return `${field.label} må aksepteres.`;
        }
        return null;
      }

      if (!isNonEmptyString(answer)) {
        return `${field.label} er påkrevd.`;
      }

      if (
        (field.type === 'select' || field.type === 'radio') &&
        Array.isArray(field.options) &&
        !field.options.includes(answer)
      ) {
        return `${field.label} har en ugyldig verdi.`;
      }

      return null;
    },
    [answers]
  );

  const getSectionValidationError = useCallback(
    (section: OthRegistrationSection | null): string | null => {
      if (!section) {
        return null;
      }

      for (const field of section.fields) {
        if (HIDDEN_FIELD_IDS.has(field.id)) {
          continue;
        }
        const error = getFieldValidationError(field);
        if (error) {
          return error;
        }
      }

      return null;
    },
    [getFieldValidationError]
  );

  const getStepValidationError = useCallback(
    (step: number): string | null => {
      if (step === 0) {
        if (!member.first_name.trim()) {
          return 'Fornavn er påkrevd.';
        }

        if (!member.surname.trim()) {
          return 'Etternavn er påkrevd.';
        }

        const formattedPhoneNumber = Utils.formatPhone(member.phone_number);
        if (!formattedPhoneNumber) {
          return 'Telefonnummer er påkrevd.';
        }
        if (!isPossiblePhoneNumber(formattedPhoneNumber)) {
          return 'Telefonnummer er ikke gyldig.';
        }

        if (!member.email.trim()) {
          return 'E-post er påkrevd.';
        }

        if (accountStep !== AccountStep.Ready) {
          if (!member.password.trim()) {
            return 'Passord er påkrevd.';
          }
          if (!isPasswordStrong(member.password)) {
            return 'Passord må være minst 8 tegn og inneholde minst ett tall og ett symbol.';
          }
        }

        if (!acceptedProgramTerms) {
          return 'Du må godta betingelser for deltakelse på programmet for å fortsette.';
        }

        if (!acceptedTerms) {
          return 'Du må akseptere personvernerklæringen for å fortsette.';
        }

        if (aboutYouSection) {
          for (const field of aboutYouSection.fields) {
            if (
              OM_DEG_MANAGED_FIELD_IDS.includes(field.id) ||
              HIDDEN_FIELD_IDS.has(field.id)
            ) {
              continue;
            }
            const error = getFieldValidationError(field);
            if (error) {
              return error;
            }
          }
        }

        return null;
      }

      if (step > 0 && step < summaryStepIndex) {
        const section = remainingSections[step - 1] ?? null;
        return getSectionValidationError(section);
      }

      return null;
    },
    [
      member.first_name,
      member.surname,
      member.phone_number,
      member.email,
      member.password,
      accountStep,
      acceptedProgramTerms,
      acceptedTerms,
      aboutYouSection,
      getFieldValidationError,
      summaryStepIndex,
      remainingSections,
      getSectionValidationError,
    ]
  );

  const createAccountValidationError = useMemo(() => {
    if (!member.first_name.trim()) {
      return 'Fornavn er påkrevd.';
    }

    if (!member.surname.trim()) {
      return 'Etternavn er påkrevd.';
    }

    const formattedPhoneNumber = Utils.formatPhone(member.phone_number);
    if (!formattedPhoneNumber) {
      return 'Telefonnummer er påkrevd.';
    }
    if (!isPossiblePhoneNumber(formattedPhoneNumber)) {
      return 'Telefonnummer er ikke gyldig.';
    }

    if (!member.email.trim()) {
      return 'E-post er påkrevd.';
    }

    if (!member.password.trim()) {
      return 'Passord er påkrevd.';
    }
    if (!isPasswordStrong(member.password)) {
      return 'Passord må være minst 8 tegn og inneholde minst ett tall og ett symbol.';
    }

    if (!acceptedProgramTerms) {
      return 'Du må godta betingelser for deltakelse på programmet for å fortsette.';
    }

    if (!acceptedTerms) {
      return 'Du må akseptere personvernerklæringen for å fortsette.';
    }

    return null;
  }, [
    member.first_name,
    member.surname,
    member.password,
    member.phone_number,
    member.email,
    acceptedProgramTerms,
    acceptedTerms,
  ]);

  const confirmAccountValidationError = useMemo(() => {
    if (!member.confirmationCode.trim()) {
      return 'Bekreftelseskode er påkrevd.';
    }
    if (member.confirmationCode.trim().length < 6) {
      return 'Bekreftelseskode må være minst 6 siffer.';
    }
    return null;
  }, [member.confirmationCode]);

  const markOmDegCompleteAndAdvance = useCallback(() => {
    setCurrentStep(1);
  }, []);

  // ─── Auth handlers ─────────────────────────────────────────────

  const handleCreateAccount = async () => {
    if (createAccountValidationError) {
      setAuthError(createAccountValidationError);
      showError(createAccountValidationError);
      return;
    }

    try {
      setIsAuthLoading(true);
      setAuthError(null);

      const formattedPhoneNumber = Utils.formatPhone(member.phone_number);
      await signUp({
        username: formattedPhoneNumber,
        password: member.password,
        options: {
          userAttributes: {
            phone_number: formattedPhoneNumber,
            ...(member.email.trim() && { email: member.email.trim() }),
          },
        },
      });

      setMember((prev) => ({
        ...prev,
        phone_number: formattedPhoneNumber,
        confirmationCode: '',
      }));
      setAccountStep(AccountStep.SignupVerification);
      setShowVerification(true);
      showSuccess('Bekreftelseskode sendt til telefonen din');
    } catch (error: any) {
      if (error?.name === 'UsernameExistsException') {
        try {
          await completeLoginAndBootstrap();
          setShowVerification(false);
          const omDegError = getStepValidationError(0);
          if (omDegError) {
            setAuthError(omDegError);
            showError(omDegError);
            return;
          }
          setAuthError(null);
          markOmDegCompleteAndAdvance();
          showSuccess('Logget inn med eksisterende konto');
          return;
        } catch {
          const message =
            'Kontoen eksisterer allerede. Bruk riktig passord for å fortsette.';
          setAuthError(message);
          showError(message);
          return;
        }
      }

      const message =
        error?.message ?? 'Kunne ikke opprette konto. Vennligst prøv igjen.';
      setAuthError(message);
      showError(message);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleConfirmAccount = async () => {
    if (confirmAccountValidationError) {
      setAuthError(confirmAccountValidationError);
      showError(confirmAccountValidationError);
      return;
    }

    try {
      setIsAuthLoading(true);
      setAuthError(null);

      const formattedPhoneNumber = Utils.formatPhone(member.phone_number);

      await confirmSignUp({
        username: formattedPhoneNumber,
        confirmationCode: member.confirmationCode.trim(),
      });

      // Set next form step before bootstrap in case auth causes a remount.
      setCurrentStep(1);
      await completeLoginAndBootstrap();
      setShowVerification(false);
      const omDegError = getStepValidationError(0);
      if (omDegError) {
        setAuthError(omDegError);
        showError(omDegError);
        return;
      }
      setAuthError(null);
      markOmDegCompleteAndAdvance();
      showSuccess('Konto bekreftet');
    } catch (error: any) {
      if (
        error?.name === 'NotAuthorizedException' &&
        typeof error?.message === 'string' &&
        error.message.toUpperCase().includes('CONFIRMED')
      ) {
        try {
          // Keep wizard progression if user is already confirmed.
          setCurrentStep(1);
          await completeLoginAndBootstrap();
          setShowVerification(false);
          const omDegError = getStepValidationError(0);
          if (omDegError) {
            setAuthError(omDegError);
            showError(omDegError);
            return;
          }
          setAuthError(null);
          markOmDegCompleteAndAdvance();
          showSuccess('Konto bekreftet');
          return;
        } catch {
          const fallbackMessage =
            'Kontoen er allerede bekreftet, men automatisk innlogging feilet.';
          setAuthError(fallbackMessage);
          showError(fallbackMessage);
          return;
        }
      }

      const message =
        error?.name === 'CodeMismatchException'
          ? 'Ugyldig bekreftelseskode.'
          : (error?.message ??
            'Kunne ikke bekrefte konto. Vennligst prøv igjen.');
      setAuthError(message);
      showError(message);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleResendConfirmationCode = async () => {
    try {
      setIsAuthLoading(true);
      setAuthError(null);

      const formattedPhoneNumber = Utils.formatPhone(member.phone_number);
      if (!formattedPhoneNumber) {
        const message = 'Telefonnummer mangler. Gå tilbake og sjekk nummeret.';
        setAuthError(message);
        showError(message);
        return;
      }

      await resendSignUpCode({
        username: formattedPhoneNumber,
      });

      showSuccess('Ny bekreftelseskode er sendt på SMS.');
    } catch (error: any) {
      const message =
        error?.message ??
        'Kunne ikke sende ny bekreftelseskode. Vennligst prøv igjen.';
      setAuthError(message);
      showError(message);
    } finally {
      setIsAuthLoading(false);
    }
  };

  // ─── Submit handler ────────────────────────────────────────────

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!schema) {
      return;
    }
    if (accountStep !== AccountStep.Ready) {
      const message = 'Fullfør kontooppsettet før du sender inn.';
      setSubmitError(message);
      showError(message);
      return;
    }

    if (validationErrors.length > 0) {
      const message = validationErrors[0];
      setSubmitError(message);
      showError(message);
      return;
    }

    // Safeguard: ensure all consent checkboxes are actively checked
    const uncheckedConsent = Array.from(CONSENT_FIELD_IDS).find(
      (id) => answers[id] !== true
    );
    if (uncheckedConsent) {
      const message = 'Du må samtykke til alle samtykkeerklæringer.';
      setSubmitError(message);
      showError(message);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await waitForBackendUserRecord();

      const firstName =
        getStringValue(answers.first_name).trim() || member.first_name.trim();
      const surname =
        getStringValue(answers.surname).trim() || member.surname.trim();
      const email = member.email.trim() || getStringValue(answers.email).trim();
      const firm = getStringValue(answers.firm).trim();

      const answersToSend: Record<string, unknown> = {
        ...answers,
        ...(email && { email }),
        accept_terms_1: acceptedProgramTerms,
        accept_terms_2: acceptedTerms,
      };

      const result = await submitOthRegistration(schema.tribe_id, {
        first_name: firstName || undefined,
        surname: surname || undefined,
        ...(email && { email }),
        ...(firm && { firm }),
        job_title: getStringValue(answers.job_title).trim() || undefined,
        industry: getStringValue(answers.industry).trim() || undefined,
        leadership_level:
          getStringValue(answers.leadership_level).trim() || undefined,
        gender: getStringValue(answers.gender).trim() || undefined,
        answers: answersToSend,
      });

      await dispatch(fetchLoggedUser());
      setSubmitResult(result);
      showSuccess('Registrering mottatt');
      sessionStorage.removeItem(getRegistrationDraftStorageKey(tribeId));
      navigate(`/circle/${result.tribeId}`);
    } catch (error) {
      const message = 'Kunne ikke sende registrering. Vennligst prøv igjen.';
      setSubmitError(message);
      showError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Navigation ────────────────────────────────────────────────

  const handleNextFromOmDeg = async () => {
    // If the user is already logged in, skip account creation & 2FA
    if (accountStep === AccountStep.Ready) {
      const omDegError = getStepValidationError(0);
      if (omDegError) {
        setAuthError(omDegError);
        showError(omDegError);
        return;
      }
      setAuthError(null);
      markOmDegCompleteAndAdvance();
      return;
    }

    // Trigger account creation → leads to 2FA interstitial
    await handleCreateAccount();
  };

  const goNext = () => {
    const stepError = getStepValidationError(currentStep);
    if (stepError) {
      if (currentStep === 0) {
        setAuthError(stepError);
      } else {
        setSubmitError(stepError);
      }
      showError(stepError);
      return;
    }

    setAuthError(null);
    setSubmitError(null);
    setCurrentStep((s) => Math.min(s + 1, summaryStepIndex));
  };

  const goBack = () => {
    if (currentStep === 0) {
      return;
    }
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === currentStep) {
      return;
    }
    // TEMP: keep all tabs freely navigable while we work on form details.
    setAuthError(null);
    setSubmitError(null);
    setCurrentStep(newValue);
  };

  // ─── Field rendering ──────────────────────────────────────────

  const renderField = (field: OthRegistrationField, disabled: boolean) => {
    const key = field.id;
    const commonRequired = field.required ? ' *' : '';

    if (field.type === 'checkbox') {
      return (
        <label className={styles.checkboxRow} key={key}>
          <input
            type="checkbox"
            checked={answers[key] === true}
            onChange={(event) =>
              setAnswers((prev) => ({ ...prev, [key]: event.target.checked }))
            }
            disabled={disabled}
          />
          <span>{field.label}</span>
        </label>
      );
    }

    if (field.type === 'radio' && Array.isArray(field.options)) {
      return (
        <div className={styles.fieldGroup} key={key}>
          <span className={styles.fieldLabel}>
            {field.label}
            {commonRequired}
          </span>
          <div className={styles.radioGroup}>
            {field.options.map((option) => (
              <label className={styles.radioRow} key={option}>
                <input
                  type="radio"
                  name={key}
                  value={option}
                  checked={answers[key] === option}
                  onChange={() =>
                    setAnswers((prev) => ({ ...prev, [key]: option }))
                  }
                  disabled={disabled}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          {field.helperText && (
            <p className={styles.helperText}>{field.helperText}</p>
          )}
        </div>
      );
    }

    const inputType = field.type === 'email' ? 'email' : 'text';

    return (
      <div className={styles.fieldGroup} key={key}>
        <label className={styles.fieldLabel} htmlFor={key}>
          {field.label}
          {commonRequired}
        </label>
        {field.type === 'textarea' ? (
          <textarea
            id={key}
            value={getStringValue(answers[key])}
            onChange={(event) =>
              setAnswers((prev) => ({ ...prev, [key]: event.target.value }))
            }
            placeholder={field.placeholder}
            disabled={disabled}
            className={styles.textarea}
          />
        ) : field.type === 'select' ? (
          <select
            id={key}
            value={getStringValue(answers[key])}
            onChange={(event) =>
              setAnswers((prev) => ({ ...prev, [key]: event.target.value }))
            }
            disabled={disabled}
            className={styles.input}
          >
            <option value="">Velg et alternativ</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={key}
            type={inputType}
            value={getStringValue(answers[key])}
            onChange={(event) =>
              setAnswers((prev) => ({ ...prev, [key]: event.target.value }))
            }
            placeholder={field.placeholder}
            disabled={disabled}
            className={styles.input}
          />
        )}
        {field.helperText && (
          <p className={styles.helperText}>{field.helperText}</p>
        )}
      </div>
    );
  };

  // ─── Render helpers ────────────────────────────────────────────

  if (isLoading) {
    return withBrand(
      <div className={styles.page}>
        <div className={styles.loader}>
          <MaterialCircularProgress />
        </div>
      </div>
    );
  }

  if (loadError || !schema) {
    return withBrand(
      <div className={styles.page}>
        <Oth360Banner title="OTH registrering" />
        <main className={styles.content}>
          <div className={styles.errorCard}>{loadError ?? 'Unknown error'}</div>
        </main>
      </div>
    );
  }

  const registrationTitle = schema.tribe_name?.trim()
    ? `${schema.tribe_name}`
    : 'OTH registrering';

  // ─── 2FA interstitial (between Om deg and Faktura) ─────────

  if (showVerification && accountStep === AccountStep.SignupVerification) {
    return withBrand(
      <div className={styles.page}>
        <Oth360Banner title={registrationTitle} />
        <main className={styles.content}>
          <div className={styles.form}>
            <section className={styles.section}>
              <h3>Bekreft konto</h3>
              <p className={styles.formDescription}>
                Vi sendte en SMS-kode til telefonen din. Skriv den inn under for
                å fortsette til registreringsskjemaet.
              </p>

              <div className={styles.accountMetaCard}>
                <span className={styles.accountMetaLabel}>Telefonnummer</span>
                <strong>{member.phone_number}</strong>
              </div>

              <div className={styles.fieldGroup}>
                <label
                  className={styles.fieldLabel}
                  htmlFor="auth_confirmation_code"
                >
                  Bekreftelseskode *
                </label>
                <input
                  id="auth_confirmation_code"
                  type="text"
                  className={styles.input}
                  value={member.confirmationCode}
                  onChange={(event) =>
                    setMember((prev) => ({
                      ...prev,
                      confirmationCode: event.target.value,
                    }))
                  }
                  disabled={isAuthLoading}
                  placeholder="6-sifret kode"
                />
              </div>

              <p className={styles.helperText}>
                Fikk du ikke SMS?{' '}
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={handleResendConfirmationCode}
                  disabled={isAuthLoading}
                >
                  Prøv på nytt
                </button>
              </p>

              {authError && <p className={styles.errorText}>{authError}</p>}

              <div className={styles.wizardNav}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={() => {
                    setShowVerification(false);
                    setAccountStep(AccountStep.SignupRequest);
                  }}
                  disabled={isAuthLoading}
                >
                  Tilbake
                </button>
                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={isAuthLoading}
                  onClick={handleConfirmAccount}
                >
                  {isAuthLoading ? 'Arbeider...' : 'Bekreft og fortsett'}
                </button>
              </div>
            </section>
          </div>
        </main>
      </div>
    );
  }

  // ─── Merged "Om deg" step (auth fields + aboutYou schema fields) ──

  const renderOmDegStep = () => {
    const disabled = isAuthLoading || isSubmitting || !!submitResult;

    return (
      <section className={styles.section}>
        <h3>Om deg</h3>

        {isCheckingAccount ? (
          <div className={styles.inlineLoader}>
            <MaterialCircularProgress />
          </div>
        ) : (
          <>
            {/* Auth fields */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="auth_first_name">
                Fornavn *
              </label>
              <input
                id="auth_first_name"
                type="text"
                className={styles.input}
                value={member.first_name}
                onChange={(event) =>
                  setMember((prev) => ({
                    ...prev,
                    first_name: event.target.value,
                  }))
                }
                disabled={disabled || accountStep === AccountStep.Ready}
                placeholder="Fornavn"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="auth_surname">
                Etternavn *
              </label>
              <input
                id="auth_surname"
                type="text"
                className={styles.input}
                value={member.surname}
                onChange={(event) =>
                  setMember((prev) => ({
                    ...prev,
                    surname: event.target.value,
                  }))
                }
                disabled={disabled || accountStep === AccountStep.Ready}
                placeholder="Etternavn"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="auth_phone_number">
                Telefonnummer *
              </label>
              <div className={styles.phoneInputWrapper}>
                <DunbarPhoneInput
                  country="no"
                  value={member.phone_number.replace(/^\+/, '')}
                  onChange={(value: string) =>
                    setMember((prev) => ({
                      ...prev,
                      phone_number: `+${value}`,
                    }))
                  }
                  disabled={disabled}
                  containerCustomClass={styles.phoneContainer}
                />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="auth_email">
                E-post *
              </label>
              <input
                id="auth_email"
                type="email"
                className={styles.input}
                value={member.email}
                onChange={(event) =>
                  setMember((prev) => ({ ...prev, email: event.target.value }))
                }
                disabled={disabled}
                placeholder="navn@eksempel.no"
              />
            </div>

            {accountStep !== AccountStep.Ready && (
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel} htmlFor="auth_password">
                  Passord *
                </label>
                <input
                  id="auth_password"
                  type="password"
                  className={styles.input}
                  value={member.password}
                  onChange={(event) =>
                    setMember((prev) => ({
                      ...prev,
                      password: event.target.value,
                    }))
                  }
                  disabled={disabled}
                  placeholder="Opprett passord"
                />
              </div>
            )}

            {/* Schema "aboutYou" fields (firma, stillingstittel, etc.) */}
            {aboutYouSection &&
              aboutYouSection.fields
                .filter((f) => !OM_DEG_MANAGED_FIELD_IDS.includes(f.id))
                .map((field) => renderField(field, disabled))}

            {/* Terms checkboxes */}
            <div className={styles.termsRow}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={acceptedProgramTerms}
                  onChange={(event) =>
                    setAcceptedProgramTerms(event.target.checked)
                  }
                  disabled={disabled}
                />
                <span>
                  Jeg godtar og bekrefter å ha lest og forstått{' '}
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalOpen(true);
                    }}
                  >
                    betingelser for deltakelse på programmet.
                  </button>
                </span>
              </label>

              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                  disabled={disabled}
                />
                <span>
                  Jeg aksepterer{' '}
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setPrivacyModalOpen(true);
                    }}
                  >
                    personvernerklæringen
                  </button>
                  .
                </span>
              </label>
            </div>

            {authError && <p className={styles.errorText}>{authError}</p>}
          </>
        )}
      </section>
    );
  };

  const renderSectionStep = (section: OthRegistrationSection) => {
    const disabled = isSubmitting || !!submitResult;

    return (
      <section className={styles.section}>
        <h3>{section.title}</h3>
        {section.description && (
          <p className={styles.formDescription}>{section.description}</p>
        )}
        {section.fields
          .filter((f) => !HIDDEN_FIELD_IDS.has(f.id))
          .map((field) => renderField(field, disabled))}
      </section>
    );
  };

  const renderSummary = () => {
    const memberOverrides: Record<string, string> = {
      first_name: member.first_name,
      surname: member.surname,
      email: member.email,
      phone_number: member.phone_number,
    };

    return (
      <section className={styles.section}>
        <h3>Sammendrag</h3>
        <p className={styles.formDescription}>
          Kontroller informasjonen din før du sender inn registreringen.
        </p>

        {visibleSections.map((section) => (
          <div key={section.id} className={styles.summarySection}>
            <h4 className={styles.summarySectionTitle}>{section.title}</h4>
            {section.fields
              .filter((f) => !HIDDEN_FIELD_IDS.has(f.id))
              .map((field) => {
                const value =
                  memberOverrides[field.id]?.trim() || answers[field.id];
                let displayValue: string;
                if (typeof value === 'boolean') {
                  displayValue = value ? 'Ja' : 'Nei';
                } else if (isNonEmptyString(value)) {
                  displayValue = value;
                } else {
                  displayValue = '—';
                }

                return (
                  <div key={field.id} className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>{field.label}</span>
                    <span className={styles.summaryValue}>{displayValue}</span>
                  </div>
                );
              })}
          </div>
        ))}

        {submitError && <p className={styles.errorText}>{submitError}</p>}
        {validationErrors.length > 0 && (
          <div className={styles.validationSummary}>
            <p className={styles.errorText}>Følgende felt må fylles ut:</p>
            <ul className={styles.errorList}>
              {validationErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </section>
    );
  };

  return withBrand(
    <div className={styles.page}>
      <Oth360Banner title={registrationTitle} />
      <main className={styles.content}>
        {/* ─── Tabs ─────────────────────────────────────────── */}
        <Box
          className={styles.tabBar}
          sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '8px' }}
        >
          <Tabs
            className={styles.tabs}
            value={currentStep}
            onChange={handleTabChange}
            aria-label="registration tabs"
            TabIndicatorProps={{ sx: { background: themeColors.text.dark100 } }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            sx={{
              '& button': { color: themeColors.text.dark70 },
              '& button.Mui-selected': { color: themeColors.text.dark100 },
            }}
          >
            {stepLabels.map((label, idx) => (
              <Tab
                key={idx}
                label={label}
                id={`reg-tab-${idx}`}
                disabled={false}
              />
            ))}
          </Tabs>
        </Box>

        {/* ─── Collapsible "Praktisk informasjon" ─────────── */}
        <div className={styles.collapsible}>
          <button
            type="button"
            className={styles.collapsibleToggle}
            onClick={() => setInfoExpanded((prev) => !prev)}
            aria-expanded={infoExpanded}
          >
            <span>Praktisk informasjon</span>
            <span
              className={`${styles.chevron} ${infoExpanded ? styles.chevronOpen : ''}`}
            >
              ▾
            </span>
          </button>
          {infoExpanded && (
            <div className={styles.collapsibleContent}>
              <h3>
                {schema?.tribe_name?.trim() ||
                  'Praktisk og krevende lederutvikling som virker'}
              </h3>
              {schema?.description?.trim() ? (
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {schema.description.trim()}
                </p>
              ) : (
                <>
                  <p>
                    Lederprogrammet Ordtilhandling er praktisk orientert, og er
                    basert på lang erfaring og kjent utviklingsteori. Utvikling
                    skjer i dyptgripende prosesser i samspill med andre ledere
                    fra store og mellomstore bedrifter i Norge. Det utvikler
                    både den enkelte leder og virksomheten.
                  </p>
                  <br></br>
                  <p>
                    Ordtilhandling leverer ikke enkeltstående og underholdende
                    kurs, men dynamiske og krevende prosesser hvor vedvarende
                    endring mot ambisiøse mål oppstår. Lederprogrammet består av
                    seks samlinger i løpet av ti måneder i unike omgivelser på
                    spahotellet Farris Bad i Larvik.
                  </p>
                  <br></br>
                  <p>
                    <i>Påmelding er bindende</i>
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* ─── Form ───────────────────────────────────────── */}
        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Step 0: Merged Om deg (auth + aboutYou fields) */}
          {currentStep === 0 && renderOmDegStep()}

          {/* Steps 1..N-1: Remaining section forms */}
          {currentSection && renderSectionStep(currentSection)}

          {/* Last step: Summary */}
          {currentStep === summaryStepIndex && renderSummary()}

          {/* Navigation buttons */}
          <div className={styles.wizardNav}>
            {currentStep > 0 && (
              <button
                type="button"
                className={styles.backButton}
                onClick={goBack}
                disabled={isSubmitting}
              >
                Tilbake
              </button>
            )}

            {currentStep === 0 && !isCheckingAccount && (
              <button
                type="button"
                className={`${styles.submitButton} ${
                  acceptedProgramTerms && acceptedTerms
                    ? styles.termsNextButtonActive
                    : styles.termsNextButton
                }`}
                onClick={handleNextFromOmDeg}
                disabled={
                  isAuthLoading || !acceptedProgramTerms || !acceptedTerms
                }
              >
                {isAuthLoading ? 'Arbeider...' : 'Neste'}
              </button>
            )}

            {currentStep > 0 && currentStep < summaryStepIndex && (
              <button
                type="button"
                className={styles.submitButton}
                onClick={goNext}
              >
                Neste
              </button>
            )}

            {currentStep === summaryStepIndex && (
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting || validationErrors.length > 0}
              >
                {isSubmitting ? 'Sender inn...' : 'Send inn registrering'}
              </button>
            )}
          </div>
        </form>

        {/* ─── Modals ─────────────────────────────────────── */}
        <MuiModal open={termsModalOpen} setOpen={setTermsModalOpen} width={760}>
          <div className={styles.privacyModalContent}>
            <p>
              Takk for din interesse. Ved aksept av tilbudet gjelder følgende
              generelle vilkår.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>Tjenester</h3>
            <p>
              Ordtilhandling AS (OTH) vil levere tjenester som beskrevet i
              tilbudet.
              <br />
              Alle priser i tilbudet er eks MVA.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>Fakturering</h3>
            <p>
              OTH fakturerer programdeltagelse ved oppstart av programmet og med
              14 dagers betalingsbetingelser. Ved forsinket betaling påløper
              renter i henhold til forsinkelsesrenteloven.
              <br />
              Kostnader tilknyttet eventuelle hotellovernattinger, betaler
              deltageren selv, direkte til hotellet ved overnatting.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>Ansvar</h3>
            <p>
              OTH er ikke ansvarlig for indirekte tap eller skader som følge av
              tjenestene.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>
              Konfidensialitet
            </h3>
            <p>
              Begge parter forplikter seg til å holde all informasjon som deles
              under samarbeidet konfidensiell. Konfidensialitetsforpliktelsen
              gjelder også etter avtalens opphør.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>
              Oppsigelse og endringer
            </h3>
            <p>
              Påmeldingen anses som bindene ved godkjenning av tilbudet. Avtalen
              kan sies opp av begge parter med et skriftlig varsel på 30 dager.
              <br />
              Dersom kunde trekker sin deltakelse senere enn 30 dager før
              programstart påløper 30% av deltakeravgiften. Alternativt kan
              kunde velge å utsette sin deltakelse til neste gjennomføring av
              samme program.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>
              Immaterielle rettigheter
            </h3>
            <p>
              Alle immaterielle rettigheter som oppstår som følge av tjenestene,
              inkludert, men ikke begrenset til opphavsrett, patenter og
              varemerker, skal tilhøre konsulentselskapet med mindre annet er
              skriftlig avtalt.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>Gjeldende lov</h3>
            <p>Avtalen reguleres av norsk lov.</p>
          </div>
        </MuiModal>

        <MuiModal
          open={privacyModalOpen}
          setOpen={setPrivacyModalOpen}
          width={760}
        >
          <div className={styles.privacyModalContent}>
            <h2 className={styles.privacyModalTitle}>
              Personvernerklæring for Ordtilhandling og Dunbar148
            </h2>
            <p className={styles.privacyModalUpdated}>
              <strong>Sist oppdatert:</strong> 18. mars 2026
            </p>
            <p>
              Denne personvernerklæringen beskriver hvordan Ordtilhandling AS
              (behandlingsansvarlig) og vår teknologipartner Dunbar App Inc.
              (databehandler) samler inn og behandler personopplysninger. Denne
              erklæringen gjelder for nye deltakere som registrerer seg via
              denne portalen.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>
              1. Roller og ansvar
            </h3>
            <ul className={styles.privacyModalList}>
              <li>
                <strong>Behandlingsansvarlig:</strong> Ordtilhandling AS har det
                overordnede ansvaret for dine personopplysninger og bestemmer
                formålet med behandlingen.
              </li>
              <li>
                <strong>Databehandler:</strong> Dunbar App Inc. (heretter
                "Dunbar") leverer den tekniske plattformen og behandler data på
                vegne av Ordtilhandling. Forholdet er regulert av en formell
                databehandleravtale (DBA) som sikrer at dine data behandles i
                samsvar med gjeldende lovverk.
              </li>
            </ul>

            <h3 className={styles.privacyModalSectionTitle}>
              2. Rettslig grunnlag (GDPR)
            </h3>
            <p>
              Vi behandler personopplysninger i henhold til den norske
              personopplysningsloven og EUs personvernforordning (GDPR).
              Behandlingen er nødvendig for å oppfylle avtalen om deltakelse i
              Ordtilhandlings programmer, samt for å ivareta berettigede
              interesser knyttet til sikkerhet og forbedring av tjenesten.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>
              3. Opplysninger som samles inn
            </h3>
            <p>
              Vi samler kun inn informasjon som er nødvendig for en god
              brukeropplevelse:
            </p>
            <ul className={styles.privacyModalList}>
              <li>
                <strong>Kontoinformasjon:</strong> Navn, e-postadresse og
                mobilnummer for identifikasjon og kommunikasjon.
              </li>
              <li>
                <strong>Profildetaljer:</strong> Fødselsår samles inn for
                aggregert statistikk og analyse for å forbedre plattformen.
                Disse dataene lagres sikkert og er ikke synlige for andre
                deltakere.
              </li>
              <li>
                <strong>Aktivitetsdata:</strong> Progresjon i utfordringer,
                notater og interaksjoner i forum/chat loggføres for å gi en
                personlig læringsreise.
              </li>
              <li>
                <strong>Tekniske data:</strong> IP-adresse og
                informasjonskapsler (cookies) brukes for sikkerhet,
                spam-deteksjon og teknisk feilsøking.
              </li>
            </ul>

            <h3 className={styles.privacyModalSectionTitle}>
              4. Deling og synlighet i plattformen
            </h3>
            <ul className={styles.privacyModalList}>
              <li>
                <strong>Sirkler og Grupper:</strong> Kommunikasjon i dine
                utvalgte grupper ("Circles") er privat og kun tilgjengelig for
                de andre deltakerne i den aktuelle gruppen.
              </li>
              <li>
                <strong>Profil:</strong> Som deltaker er din profil som
                hovedregel synlig for andre brukere i systemet. Du kan selv
                velge å være en "usynlig deltaker" via profilinnstillingene i
                appen.
              </li>
              <li>
                <strong>Underleverandører:</strong> Data lagres på sikre servere
                hos Amazon AWS. Vi selger aldri dine data til tredjeparter og
                bruker dem ikke til ekstern markedsføring.
              </li>
            </ul>

            <h3 className={styles.privacyModalSectionTitle}>
              5. Dine rettigheter
            </h3>
            <p>Som deltaker har du omfattende rettigheter etter GDPR:</p>
            <ul className={styles.privacyModalList}>
              <li>
                <strong>Innsyn og retting:</strong> Du kan når som helst se og
                endre dine opplysninger i appen.
              </li>
              <li>
                <strong>Sletting ("Retten til å bli glemt"):</strong> Du kan når
                som helst be om sletting av din konto. Dataene vil bli permanent
                slettet fra våre servere innen 72 timer, med mindre vi er
                lovpålagt å oppbevare dem (f.eks. av hensyn til
                bokføringsloven).
              </li>
              <li>
                <strong>Dataportabilitet:</strong> Du har rett til å få utlevert
                dine data i et maskinlesbart format.
              </li>
              <li>
                <strong>Klagerett:</strong> Du har rett til å klage til
                Datatilsynet dersom du mener vår behandling av
                personopplysninger ikke er i samsvar med regelverket.
              </li>
            </ul>

            <h3 className={styles.privacyModalSectionTitle}>
              6. Lagringstid og sikkerhet
            </h3>
            <p>
              Personopplysninger lagres så lenge du er en aktiv deltaker. Ved
              inaktivitet over lengre tid eller ved avslutning av kundeforholdet
              mellom Ordtilhandling og Dunbar, vil dataene anonymiseres eller
              slettes i tråd med våre sletterutiner. Vi bruker kryptering og
              strenge tilgangskontroller for å beskytte dine data.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>7. Endringer</h3>
            <p>
              Vi forbeholder oss retten til å oppdatere denne erklæringen. Ved
              vesentlige endringer som påvirker ditt personvern, vil du få
              varsel direkte i plattformen eller via e-post.
            </p>

            <h3 className={styles.privacyModalSectionTitle}>8. Kontakt</h3>
            <p>Har du spørsmål om personvern?</p>
            <ul className={styles.privacyModalList}>
              <li>
                <strong>Ordtilhandling AS:</strong>{' '}
                <a
                  href="mailto:stian@ordtilhandling.no"
                  className={styles.privacyEmailLink}
                >
                  stian@ordtilhandling.no
                </a>
              </li>
              <li>
                <strong>Dunbar App Inc. (Privacy Officer):</strong>{' '}
                <a
                  href="mailto:privacy@dunbar-app.com"
                  className={styles.privacyEmailLink}
                >
                  privacy@dunbar-app.com
                </a>
              </li>
            </ul>
          </div>
        </MuiModal>
      </main>
    </div>
  );
};
